import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import type { TFunction } from 'i18next';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Youtube from '@tiptap/extension-youtube';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Undo2,
  Video,
} from 'lucide-react';
import { filesUploader } from '../MediaUpload/helpers';
import { ImageInsertDialog } from './ImageInsertDialog';

/** Default image uploader — POSTs to the real file service and returns the public URL. */
async function defaultUploadImage(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const { url } = await filesUploader(file, onProgress);
  return url;
}

/**
 * Upload a pasted/dropped image then insert it at the current selection. Runs
 * async behind a loading toast; the editor stays usable while it uploads.
 */
function uploadAndInsertAtSelection(
  view: EditorView,
  file: File,
  upload: (file: File, onProgress?: (percent: number) => void) => Promise<string>,
  t: TFunction<'components'>,
) {
  const toastId = toast.loading(t('richTextEditor.imageUploading'));
  upload(file)
    .then((src) => {
      toast.dismiss(toastId);
      if (!src) {
        toast.error(t('richTextEditor.imageUploadError'));
        return;
      }
      const { state } = view;
      const imageType = state.schema.nodes.image;
      if (!imageType) return;
      view.dispatch(state.tr.replaceSelectionWith(imageType.create({ src })));
    })
    .catch(() => {
      toast.dismiss(toastId);
      toast.error(t('richTextEditor.imageUploadError'));
    });
}

interface RichTextEditorProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  /**
   * Custom media picker for the image toolbar button. When provided it takes
   * precedence over the built-in upload/URL dialog (e.g. to open a media
   * library). Returns the URL to insert, or null to cancel.
   */
  onInsertMedia?: () => Promise<string | null> | string | null;
  /**
   * Uploads an image file and resolves its public URL. Defaults to the app's
   * file service; override for tests or alternate storage.
   */
  uploadImage?: (file: File, onProgress?: (percent: number) => void) => Promise<string>;
  disabled?: boolean;
}

function ToolbarButton({
  active,
  onClick,
  children,
  label,
  disabled,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function Toolbar({
  editor,
  onInsertMedia,
  onOpenImageDialog,
}: {
  editor: Editor;
  onInsertMedia?: RichTextEditorProps['onInsertMedia'];
  onOpenImageDialog: () => void;
}) {
  const { t } = useTranslation('components');
  const promptLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt(t('richTextEditor.linkPrompt'), previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const promptImage = async () => {
    // A custom picker wins; otherwise open the built-in upload + URL dialog.
    if (!onInsertMedia) {
      onOpenImageDialog();
      return;
    }
    const picked = await onInsertMedia();
    if (picked) editor.chain().focus().setImage({ src: picked }).run();
  };

  const promptVideo = () => {
    const url = window.prompt(t('richTextEditor.videoPrompt'));
    if (url) editor.commands.setYoutubeVideo({ src: url });
  };

  return (
    <div className="ds-rte__toolbar" role="toolbar" aria-label={t('richTextEditor.toolbarLabel')}>
      <ToolbarButton label={t('richTextEditor.undo')} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={14} /></ToolbarButton>
      <ToolbarButton label={t('richTextEditor.redo')} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={14} /></ToolbarButton>
      <span className="ds-rte__toolbar-sep" />
      <ToolbarButton
        label={t('richTextEditor.heading2')}
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 size={14} />
      </ToolbarButton>
      <ToolbarButton
        label={t('richTextEditor.heading3')}
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 size={14} />
      </ToolbarButton>
      <span className="ds-rte__toolbar-sep" />
      <ToolbarButton label={t('richTextEditor.bold')} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.italic')} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.strike')} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.code')} active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
        <Code size={14} />
      </ToolbarButton>
      <span className="ds-rte__toolbar-sep" />
      <ToolbarButton label={t('richTextEditor.bulletList')} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.orderedList')} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.blockquote')} active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote size={14} />
      </ToolbarButton>
      <span className="ds-rte__toolbar-sep" />
      <ToolbarButton label={t('richTextEditor.link')} active={editor.isActive('link')} onClick={promptLink}>
        <Link2 size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.image')} onClick={() => void promptImage()}>
        <ImageIcon size={14} />
      </ToolbarButton>
      <ToolbarButton label={t('richTextEditor.video')} onClick={promptVideo}>
        <Video size={14} />
      </ToolbarButton>
      <ToolbarButton
        label={t('richTextEditor.table')}
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      >
        <TableIcon size={14} />
      </ToolbarButton>
    </div>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  maxLength = 50_000,
  onInsertMedia,
  uploadImage = defaultUploadImage,
  disabled,
}: RichTextEditorProps) {
  const { t } = useTranslation('components');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Refs keep the paste/drop handlers (created once with the editor) pointing
  // at the latest uploader and translator without recreating the editor.
  const uploadRef = useRef(uploadImage);
  const tRef = useRef(t);
  useEffect(() => {
    uploadRef.current = uploadImage;
    tRef.current = t;
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Image,
      Placeholder.configure({ placeholder: placeholder ?? t('richTextEditor.placeholder') }),
      CharacterCount.configure({ limit: maxLength }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Youtube.configure({ controls: true }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      // Upload images pasted from the clipboard.
      handlePaste: (view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []).filter((f) =>
          f.type.startsWith('image/'),
        );
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => uploadAndInsertAtSelection(view, f, uploadRef.current, tRef.current));
        return true;
      },
      // Upload images dropped onto the editor.
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []).filter((f) =>
          f.type.startsWith('image/'),
        );
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => uploadAndInsertAtSelection(view, f, uploadRef.current, tRef.current));
        return true;
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) editor.commands.setContent(value || '', false);
  }, [value, editor]);

  if (!editor) return null;

  const charStorage = editor.storage.characterCount as { characters?: () => number } | undefined;
  const count: number = charStorage?.characters?.() ?? 0;
  const over = count > maxLength;

  return (
    <div className="ds-rte" aria-disabled={disabled}>
      <Toolbar
        editor={editor}
        onInsertMedia={onInsertMedia}
        onOpenImageDialog={() => setImageDialogOpen(true)}
      />
      <EditorContent editor={editor} className="ds-rte__body" />
      <div className="ds-rte__footer" data-over={over ? 'true' : 'false'}>
        <span>{t('richTextEditor.charCount', { count: count.toLocaleString('tr-TR'), max: maxLength.toLocaleString('tr-TR') })}</span>
        <span>{disabled ? t('richTextEditor.readOnly') : t('richTextEditor.markdownEnabled')}</span>
      </div>
      <ImageInsertDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        uploadImage={uploadImage}
        onInsert={(src) => editor.chain().focus().setImage({ src }).run()}
      />
    </div>
  );
}
